/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Mega Menu Sails V3 — editorial panel: compact dropdown, hover zones, Escape, keyboard nav
 */
(function () {
  var EDITORIAL_CLOSE_DELAY = 220;

  function getMenuActivator(menu) {
    return document.querySelector('[aria-controls="' + menu.id + '"][data-open-on-hover]');
  }

  function isInsideEditorialHoverZone(menu, target) {
    if (!target || typeof target.closest !== "function") return false;
    var activator = getMenuActivator(menu);
    var triggerItem = activator ? activator.closest("li") : null;
    return (
      menu.contains(target) ||
      target === activator ||
      (activator && activator.contains(target)) ||
      (triggerItem && triggerItem.contains(target))
    );
  }

  function clearEditorialCloseTimer(menu) {
    clearTimeout(menu._editorialHoverClose);
    menu._editorialHoverClose = null;
  }

  function scheduleEditorialClose(menu) {
    clearEditorialCloseTimer(menu);
    menu._editorialHoverClose = setTimeout(function () {
      if (menu.hasAttribute("open")) menu.hide();
    }, EDITORIAL_CLOSE_DELAY);
  }

  function initEditorialOpenState(menu, ac) {
    menu.addEventListener(
      "dialog:before-show",
      function () {
        document.documentElement.classList.add("mega-menu-editorial-open");
        menu.style.setProperty("--mm-top", menu.getBoundingClientRect().top + "px");
      },
      { signal: ac.signal }
    );
    menu.addEventListener(
      "dialog:after-hide",
      function () {
        document.documentElement.classList.remove("mega-menu-editorial-open");
        clearEditorialCloseTimer(menu);
      },
      { signal: ac.signal }
    );
    ac.signal.addEventListener("abort", function () {
      document.documentElement.classList.remove("mega-menu-editorial-open");
      clearEditorialCloseTimer(menu);
    });
  }

  function initEditorialHoverZone(menu, ac) {
    menu.addEventListener(
      "pointerenter",
      function () {
        clearEditorialCloseTimer(menu);
      },
      { signal: ac.signal }
    );
    menu.addEventListener(
      "pointerleave",
      function (event) {
        if (!menu.hasAttribute("open")) return;
        if (isInsideEditorialHoverZone(menu, event.relatedTarget)) return;
        scheduleEditorialClose(menu);
      },
      { signal: ac.signal }
    );
    document.addEventListener(
      "pointermove",
      function (event) {
        if (!menu.hasAttribute("open")) return;
        if (isInsideEditorialHoverZone(menu, event.target)) {
          clearEditorialCloseTimer(menu);
        }
      },
      { signal: ac.signal }
    );
  }

  function initScrollLock(menu, ac) {
    if (menu.scrollLockInitialized) return;
    menu.scrollLockInitialized = true;

    if (isEditorialMenu(menu)) {
      initEditorialOpenState(menu, ac);
      initEditorialHoverZone(menu, ac);
      return;
    }

    menu.addEventListener(
      "dialog:before-show",
      function () {
        document.documentElement.classList.add("mega-menu-v3-open");
        menu.style.setProperty("--mm-top", menu.getBoundingClientRect().top + "px");
      },
      { signal: ac.signal }
    );
    menu.addEventListener(
      "dialog:after-hide",
      function () {
        document.documentElement.classList.remove("mega-menu-v3-open");
      },
      { signal: ac.signal }
    );
    ac.signal.addEventListener("abort", function () {
      document.documentElement.classList.remove("mega-menu-v3-open");
    });
  }

  function isEditorialMenu(menu) {
    return menu.classList && typeof menu.classList.contains === "function"
      ? menu.classList.contains("mega-menu-sails--editorial")
      : false;
  }

  function initKeyboardNav(menu, ac) {
    menu.addEventListener(
      "keydown",
      (event) => {
        if (!menu.hasAttribute("open")) return;
        const focusables = menu.querySelectorAll('a[href]:not([tabindex="-1"]), button:not([disabled])');
        const items = Array.from(focusables);
        const index = items.indexOf(document.activeElement);
        if (index === -1) return;

        let next = index;
        if (event.key === "ArrowDown" || event.key === "ArrowRight") {
          next = Math.min(index + 1, items.length - 1);
        } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
          next = Math.max(index - 1, 0);
        } else {
          return;
        }

        event.preventDefault();
        items[next].focus();
      },
      { signal: ac.signal }
    );
  }

  function initV3Menu(menu) {
    if (menu.v3Initialized) return;
    menu.v3Initialized = true;

    const section = menu.closest(".shopify-section");
    const ac = new AbortController();
    const sig = { signal: ac.signal };

    initScrollLock(menu, ac);
    initKeyboardNav(menu, ac);

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Escape" || !menu.hasAttribute("open")) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const trigger = document.querySelector('[aria-controls="' + menu.id + '"]');
        menu.focusTrap?.deactivate({ returnFocus: false, onDeactivate: () => {} });
        menu.hide().then(() => {
          if (!menu.hasAttribute("open") && menu.isConnected) trigger?.focus();
        });
      },
      sig
    );

    document.addEventListener(
      "shopify:section:unload",
      (event) => {
        if (event.target !== section && !event.target.contains(menu)) return;
        ac.abort();
        menu.v3Initialized = false;
        menu.scrollLockInitialized = false;
      },
      sig
    );
  }

  function init() {
    document.querySelectorAll("[data-v3-panels]").forEach(initV3Menu);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("loftsails:menu:loaded", init);
})();
